import React from 'react'
import { Center, CenterData } from '@/types/models'
import { isNonEmptyString, isNull, isNumber } from '@/utils/checks'
import { adjustSponsorTier } from '@/domain/useCases/score/adjustSponsorTier'
import { faInfoCircle, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatAdminLink } from '@/domain/useCases/adminBarInfo/adminBarUtils'

type CenterAlgoliaData = Center & {
  _rankingInfo: {
    filters: number
  }
  resultPosition: number
}

const adminInfoBrowse: React.FC = ({
  center,
  termFocus,
  position
}: {
  center: CenterAlgoliaData
  termFocus?: string
  position: number
}) => {
  const cardPosition = (center, position) => {
    if (!isNumber(center?.position)) {
      return null
    }

    return (
      <div className="card-position sticky right-2 top-1 pr-2 text-sm text-primary-700">
        <span className="position-value">
          <span className="mr-1">in results: </span> {center.resultPosition}
        </span>
        <span className="mr-1">Placement:</span>{' '}
        <span className="position-value">{position + 1}</span>
      </div>
    )
  }

  const budgetPacing = (center) => {
    if (isNull(center?.budget_pacing)) {
      return null
    }
    return (
      <div className="flex-col flex-wrap md:flex-row">
        <span className="flex underline">adRank</span>
        <span className="pacing-value mx-0.5 flex">
          {center?.budget_pacing}
        </span>
      </div>
    )
  }

  const sponsorTier = (center) => {
    if (!isNumber(center?.sponsor_tier)) {
      return null
    }
    return (
      <div className="sponsor-tier hidden flex-col sm:flex ">
        <span className="mr-0.5 whitespace-nowrap underline">
          sponsor tier:{' '}
          <span className="sponsor-tier-value font-semibold no-underline">
            {center.sponsor_tier}{' '}
          </span>
        </span>
        <span className="sponsor-tier-boost text-center font-semibold">
          {' '}
          +{adjustSponsorTier(center.sponsor_tier)}
        </span>{' '}
      </div>
    )
  }

  const qualityScore = (center) => {
    if (!isNumber(center.qualityScore || center.quality_score)) {
      return null
    }
    return (
      <div className="mx-0.5  hidden flex-col flex-wrap items-center whitespace-break-spaces  sm:flex ">
        <span className="mr-0.5 text-clip whitespace-nowrap underline">
          quality score
        </span>
        <span className="quality-score-value font-semibold">
          {center.qualityScore || center.quality_score}
        </span>
      </div>
    )
  }
  const primaryFocus = (center, termFocus) => {
    if (!termFocus) {
      return null
    }
    return (
      <div
        className={`hidden flex-col sm:flex md:h-10 ${
          center?.primaryFocus?.includes(termFocus)
            ? 'ring-offset-opacity-50 ring-opacity/40 bg-[#34d399] text-white ring ring-[#34d399] ring-offset-1 ring-offset-[#34d399]'
            : 'ring-offset-opacity-50 ring-opacity/40 bg-[#f87171] text-white ring ring-[#f87171]  ring-offset-1 ring-offset-[#f87171]'
        }`}
      >
        <span className="justify-center whitespace-nowrap text-center underline">
          Primary Focus
        </span>
        <span className={`focus-match-value text-ellipsis`}>
          {center?.primaryFocus?.includes(termFocus)
            ? `👍 ${termFocus}`
            : `👎 ${termFocus}`}
        </span>
      </div>
    )
  }

  const browseScore = (center: CenterAlgoliaData) => {
    if (
      !isNumber(center.score) ||
      !center.fromBrowseSearch ||
      !isNumber(center?._rankingInfo?.filters)
    ) {
      return null
    }
    return (
      <div
        className={`mt-[-12px] flex flex-col gap-y-1 text-center sm:max-w-[200px] md:text-left`}
      >
        <div className="group relative after:hidden after:hover:block">
          <span className="rankingInfo inline-flex whitespace-break-spaces border-b-2 border-dashed border-primary-150 px-1 pt-1 md:whitespace-nowrap">
            <span className="">Filter Score: </span>
            <span className="filterScore pl-1">
              {center?._rankingInfo?.filters}
            </span>
          </span>
          <div className="absolute bottom-full mb-2 line-clamp-3 hidden rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 group-hover:block">
            <span className="text-sm text-gray-700">
              Filter score comes from Algolia. It is the sum of all matched
              optional filters the center meets. This tells us if the result
              should be in the first group.
            </span>
          </div>
        </div>
        <div className="group relative after:hidden after:hover:block">
          <span
            className={`sorting-score-value inline-flex whitespace-break-spaces pr-0.5 md:whitespace-nowrap`}
          >
            Feed Score:{' '}
            <span className={`sorting-score-value pl-1`}>
              {''}
              {center?.score?.toFixed(4)}
            </span>
          </span>
          <div className="absolute bottom-full mb-2 line-clamp-3 hidden rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 group-hover:block">
            <span className="text-sm text-gray-700">
              Feed score applies the sorting logic after we retrieve the result.
              The boosts on visible on this row all affect the feed score.
            </span>
          </div>
        </div>
      </div>
    )
  }
  const adminLinkButton = (center) => {
    if (!isNonEmptyString(center?.slug)) {
      return null
    }
    return (
      <div className="admin-link-button mt-2.5 hidden max-h-5 sm:flex sm:flex-row sm:items-end sm:justify-end">
        <a
          className="edit-center flex flex-row px-2 text-primary-800 no-underline hover:rounded-sm hover:shadow-md"
          href={formatAdminLink(center)}
          target="_blank"
          rel="noreferrer"
        >
          <FontAwesomeIcon icon={faPenToSquare} />
          <span className="ml-2">Edit</span>
        </a>
      </div>
    )
  }

  return (
    <div className="grid max-h-40 grid-cols-[fit-content]">
      <div className="browse-value-row flex w-full flex-nowrap justify-between px-5">
        {budgetPacing(center)}
        {primaryFocus(center, termFocus)}
        {sponsorTier(center)}
        {qualityScore(center)}
        {browseScore(center)}
        <div className="mr-3 flex flex-col self-center">
          {adminLinkButton(center)}
        </div>
      </div>
    </div>
  )
}

export default adminInfoBrowse
